import { useState, useEffect, memo, type FC } from 'react';
import { CounterCard } from '../ui';
import { CounterTable } from './CounterTable';
import type { ColumnsType } from 'antd/es/table';
import { useBreakpoint } from '@smolt/shared/hooks';

/* eslint-disable @typescript-eslint/no-explicit-any -- Generic table props require any for compatibility with AntD */
export interface CounterHistoryCardProps {
  columns: ColumnsType<any>;
  dataSource: any[];
  loading?: boolean;
  isMobile?: boolean;
  onRowClick?: (record: any) => void;
  selectedRecordKey?: string | null;
}
/* eslint-enable @typescript-eslint/no-explicit-any */

/**
 * CounterHistoryCard - Counter Page History Card Component
 * Displays historical counting data in a table
 * Memoized to prevent unnecessary re-renders when props haven't changed
 */
export const CounterHistoryCard: FC<CounterHistoryCardProps> = memo(
  ({ columns, dataSource, loading = false, isMobile = false, onRowClick, selectedRecordKey }) => {
    const breakpoint = useBreakpoint();
    const [currentPage, setCurrentPage] = useState(1);
    const [windowSize, setWindowSize] = useState(() => {
      if (typeof window !== 'undefined') {
        return { width: window.innerWidth, height: window.innerHeight };
      }
      return { width: 0, height: 0 };
    });
    const pageSize = 10;
    const totalPages = Math.ceil((dataSource?.length || 0) / pageSize);

    // Track window size for 800x500 detection
    useEffect(() => {
      const updateSize = () => {
        setWindowSize({ width: window.innerWidth, height: window.innerHeight });
      };
      updateSize();
      window.addEventListener('resize', updateSize);
      return () => window.removeEventListener('resize', updateSize);
    }, []);

    // Responsive pagination styles (matching table cell clamp pattern)
    // Use mobile values (clamp min) for 800x500 or smaller screens
    const isSmallScreen = windowSize.width <= 800 && windowSize.height <= 500;
    const isDesktop = breakpoint !== 'mobile' && !isSmallScreen;
    const paginationBtnSize = isMobile
      ? 'clamp(14px, 5vw, 20px)'
      : isDesktop
        ? 'clamp(16px, 2.5vw, 32px)'
        : '16px';
    const paginationBtnFontSize = isMobile
      ? 'clamp(7px, 2.5vw, 10px)'
      : isDesktop
        ? 'clamp(8px, 2.5vw, 22px)'
        : '8px';
    const paginationPageFontSize = isMobile
      ? 'clamp(7px, 2.5vw, 10px)'
      : isDesktop
        ? 'clamp(8px, 2vw, 16px)'
        : '8px';
    const paginationPadding = isMobile
      ? 'clamp(3px, 1.2vw, 5px)'
      : isDesktop
        ? 'clamp(4px, 1vw, 10px)'
        : '4px';

    // Calculate scroll height based on row height
    // Row height: mobile=18px, tablet=15px (10 rows), 800px+=clamp(20px, 2.5vw, 40px)
    // Variable rows displayed based on breakpoint
    const getScrollHeight = () => {
      if (isMobile || breakpoint === 'mobile') {
        return 10 * 18; // 180px
      }
      if (breakpoint === 'tablet') {
        return 10 * 15; // 150px for tablet (10 rows, minimal height)
      }
      // For 800px+, row height is clamp(20px, 2.5vw, 40px)
      // Use max row height (40px) to minimize scrolling
      return 10 * 40; // 400px for larger screens
    };

    // Calculate visible page range (5 pages at a time)
    const startPage = Math.floor((currentPage - 1) / 5) * 5 + 1;
    const endPage = Math.min(startPage + 4, totalPages);

    // Reset to page 1 when dataSource changes
    useEffect(() => {
      setCurrentPage(1);
    }, [dataSource?.length]);

    // Get current page data
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const currentData = dataSource?.slice(startIndex, endIndex) || [];

    // Generate page numbers to display (exactly 5 or less)
    const pageNumbers: number[] = [];
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    const handlePrevPage = () => {
      if (currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    };

    const handleNextPage = () => {
      if (currentPage < totalPages) {
        setCurrentPage(currentPage + 1);
      }
    };

    const handleJumpPrev = () => {
      const newPage = Math.max(1, currentPage - 5);
      setCurrentPage(newPage);
    };

    const handleJumpNext = () => {
      const newPage = Math.min(totalPages, currentPage + 5);
      setCurrentPage(newPage);
    };

    const handlePageClick = (page: number) => {
      setCurrentPage(page);
    };

    return (
      <CounterCard
        variant="light"
        padding="none"
        className="overflow-hidden"
      >
        <div
          className="flex flex-col"
          style={{ padding: breakpoint === 'tablet' ? '2px 6px 12px 6px' : '4px 8px 24px 8px' }}
        >
          <h3
            style={{
              fontFamily: 'Noto Sans',
              fontWeight: 700,
              fontSize: isMobile ? 'clamp(12px, 3.8vw, 18px)' : 'clamp(12px, 3cqi, 20px)',
              lineHeight: isMobile ? 'clamp(18px, 5.7vw, 27px)' : 'clamp(18px, 4.5cqi, 30px)',
              letterSpacing: '0%',
              textAlign: 'left',
              verticalAlign: 'middle',
              color: '#8E8E93',
              margin: 0,
              marginBottom: isMobile ? 'clamp(8px, 2.5vw, 14px)' : 'clamp(8px, 1.6cqh, 20px)',
            }}
          >
            History
          </h3>
          <div
            className="flex flex-col"
            style={{ flex: '0 0 auto' }}
          >
            <CounterTable
              columns={columns}
              dataSource={currentData}
              loading={loading}
              pagination={false}
              rowKey={(record) =>
                String(record?.id ?? record?.key ?? `row-${record.time ?? Math.random()}`)
              }
              size="small"
              scroll={{ y: getScrollHeight() }}
              isMobile={isMobile}
              rowClassName={(record) =>
                selectedRecordKey && record?.key === selectedRecordKey ? 'row-selected' : ''
              }
              onRow={
                onRowClick
                  ? (record) => ({
                      onClick: () => onRowClick(record),
                      style: { cursor: 'pointer' },
                    })
                  : undefined
              }
            />
            {/* Custom Pagination */}
            <div
              className="custom-pagination"
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '3px',
                marginTop: breakpoint === 'tablet' ? '6px' : '17px',
                padding: breakpoint === 'tablet' ? '6px 0' : `${paginationPadding} 0`,
                height: '16px',
              }}
            >
              {/* Jump Prev Button (« - 5 pages back) */}
              <button
                onClick={handleJumpPrev}
                disabled={currentPage <= 5}
                className="pagination-btn"
                style={{
                  width: paginationBtnSize,
                  height: paginationBtnSize,
                  padding: '0',
                  border: 'none',
                  background: 'transparent',
                  cursor: currentPage <= 5 ? 'not-allowed' : 'pointer',
                  color: currentPage <= 5 ? '#d9d9d9' : '#48484A',
                  fontFamily: 'Noto Sans',
                  fontWeight: 600,
                  fontSize: paginationBtnFontSize,
                  lineHeight: '1',
                  letterSpacing: '0%',
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  alignSelf: 'center',
                  transform: 'translateY(-1px)',
                  outline: 'none',
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                «
              </button>

              {/* Prev Button (‹ - 1 page back) */}
              <button
                onClick={handlePrevPage}
                disabled={currentPage === 1}
                className="pagination-btn"
                style={{
                  width: paginationBtnSize,
                  height: paginationBtnSize,
                  padding: '0',
                  border: 'none',
                  background: 'transparent',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  color: currentPage === 1 ? '#d9d9d9' : '#48484A',
                  fontFamily: 'Noto Sans',
                  fontWeight: 600,
                  fontSize: paginationBtnFontSize,
                  lineHeight: '1',
                  letterSpacing: '0%',
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  alignSelf: 'center',
                  transform: 'translateY(-1px)',
                  outline: 'none',
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                ‹
              </button>

              {/* Page Numbers */}
              {pageNumbers.map((pageNum) => (
                <button
                  key={pageNum}
                  onClick={() => handlePageClick(pageNum)}
                  className={`pagination-page ${pageNum === currentPage ? 'active' : ''}`}
                  style={{
                    width: paginationBtnSize,
                    height: paginationBtnSize,
                    minWidth: paginationBtnSize,
                    padding: '0',
                    border: 'none',
                    borderRadius: '50%',
                    background: pageNum === currentPage ? '#007AFF' : 'transparent',
                    color: pageNum === currentPage ? '#FFFFFF' : '#48484A',
                    cursor: 'pointer',
                    fontFamily: 'Noto Sans',
                    fontWeight: 600,
                    fontSize: paginationPageFontSize,
                    lineHeight: '1',
                    letterSpacing: '0%',
                    textAlign: 'center',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    alignSelf: 'center',
                    outline: 'none',
                    WebkitTapHighlightColor: 'transparent',
                    transform: isMobile ? 'translateY(-1px)' : 'translateY(2px)',
                  }}
                >
                  {pageNum}
                </button>
              ))}

              {/* Next Button (› - 1 page forward) */}
              <button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
                className="pagination-btn"
                style={{
                  width: paginationBtnSize,
                  height: paginationBtnSize,
                  padding: '0',
                  border: 'none',
                  background: 'transparent',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  color: currentPage === totalPages ? '#d9d9d9' : '#48484A',
                  fontFamily: 'Noto Sans',
                  fontWeight: 600,
                  fontSize: paginationBtnFontSize,
                  lineHeight: '1',
                  letterSpacing: '0%',
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  alignSelf: 'center',
                  transform: 'translateY(-1px)',
                  outline: 'none',
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                ›
              </button>

              {/* Jump Next Button (» - 5 pages forward) */}
              <button
                onClick={handleJumpNext}
                disabled={currentPage + 5 > totalPages}
                className="pagination-btn"
                style={{
                  width: paginationBtnSize,
                  height: paginationBtnSize,
                  padding: '0',
                  border: 'none',
                  background: 'transparent',
                  cursor: currentPage + 5 > totalPages ? 'not-allowed' : 'pointer',
                  color: currentPage + 5 > totalPages ? '#d9d9d9' : '#48484A',
                  fontFamily: 'Noto Sans',
                  fontWeight: 600,
                  fontSize: paginationBtnFontSize,
                  lineHeight: '1',
                  letterSpacing: '0%',
                  textAlign: 'center',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  alignSelf: 'center',
                  transform: 'translateY(-1px)',
                  outline: 'none',
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                »
              </button>
            </div>
          </div>
        </div>
      </CounterCard>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison: only re-render if props actually changed
    // Check first record's real-time values (count, duration, weight) for counting updates
    const firstRecordChanged = () => {
      const prevFirst = prevProps.dataSource[0];
      const nextFirst = nextProps.dataSource[0];
      if (!prevFirst && !nextFirst) return false;
      if (!prevFirst || !nextFirst) return true;
      return (
        prevFirst.count !== nextFirst.count ||
        prevFirst.duration !== nextFirst.duration ||
        prevFirst.weight !== nextFirst.weight
      );
    };

    // Deep comparison for dataSource array (check length and key values)
    const dataSourceChanged =
      prevProps.dataSource.length !== nextProps.dataSource.length ||
      firstRecordChanged() ||
      prevProps.dataSource.some((item, index) => {
        const nextItem = nextProps.dataSource[index];
        return !nextItem || item.key !== nextItem.key;
      });

    return (
      !dataSourceChanged &&
      prevProps.columns === nextProps.columns &&
      prevProps.loading === nextProps.loading &&
      prevProps.isMobile === nextProps.isMobile &&
      prevProps.onRowClick === nextProps.onRowClick &&
      prevProps.selectedRecordKey === nextProps.selectedRecordKey
    );
  }
);

CounterHistoryCard.displayName = 'CounterHistoryCard';
